import { useState, useCallback, useRef, type ChangeEvent } from 'react'
import { Upload, ChevronRight, ChevronLeft, CheckCircle, FileSpreadsheet, FileText, Package, Layers } from 'lucide-react'
import { AppIcon } from '@/components/ui/app-icon'
import { Modal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { useFilePreview } from '@/hooks/useFileParser'
import type { SeparatorType, FilePreviewResult, ParseConfig } from '@/types'
import { SEPARATOR_LABELS, DEFAULT_PARSE_CONFIG } from '@/types'

// ═══════════════════════════════════════════
// Types
// ═══════════════════════════════════════════

type Step = 'file' | 'preview' | 'submit'

interface FileImportModalProps {
  isOpen: boolean
  onClose: () => void
  /** For ka_box_pallet: allows 2-file mode */
  needsTwoFiles?: boolean
  codeType: string
  onSubmit: (data: FileImportData) => void
  isSubmitting?: boolean
}

export interface FileImportData {
  fileBoxes: File
  filePallets?: File
  config: ParseConfig
}

// ═══════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════

const STORAGE_KEY_PREFIX = 'truemark_file_import_'

function loadSavedConfig(codeType: string): Partial<ParseConfig> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + codeType)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function saveConfig(codeType: string, config: ParseConfig) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + codeType, JSON.stringify(config))
  } catch { /* ignore */ }
}

// ═══════════════════════════════════════════
// Component
// ═══════════════════════════════════════════

export function FileImportModal({
  isOpen,
  onClose,
  needsTwoFiles = false,
  codeType,
  onSubmit,
  isSubmitting = false,
}: FileImportModalProps) {
  // Key resets inner component state when modal re-opens
  if (!isOpen) return null
  return (
    <FileImportModalInner
      key={codeType}
      isOpen={isOpen}
      onClose={onClose}
      needsTwoFiles={needsTwoFiles}
      codeType={codeType}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    />
  )
}

function FileImportModalInner({
  isOpen,
  onClose,
  needsTwoFiles = false,
  codeType,
  onSubmit,
  isSubmitting = false,
}: FileImportModalProps) {
  const previewMutation = useFilePreview()

  // State
  const [step, setStep] = useState<Step>('file')
  const [fileBoxes, setFileBoxes] = useState<File | null>(null)
  const [filePallets, setFilePallets] = useState<File | null>(null)
  const [fileMode, setFileMode] = useState<'two_files' | 'single_file'>('two_files')
  const [preview, setPreview] = useState<FilePreviewResult | null>(null)
  const [previewPallets, setPreviewPallets] = useState<FilePreviewResult | null>(null)
  const [previewTab, setPreviewTab] = useState<'boxes' | 'pallets'>('boxes')
  const [config, setConfig] = useState<ParseConfig>(() => ({
    ...DEFAULT_PARSE_CONFIG,
    ...loadSavedConfig(codeType),
  }))
  /** Which column target is being selected (for single-file dual-code mode) */
  const [columnTarget, setColumnTarget] = useState<'boxes' | 'pallets'>('boxes')

  /** Whether the user needs to pick two separate columns (single file + ka_box_pallet) */
  const isDualColumnMode = needsTwoFiles && fileMode === 'single_file'

  const fileBoxesInputRef = useRef<HTMLInputElement>(null)
  const filePalletsInputRef = useRef<HTMLInputElement>(null)

  // ── Handlers ──────────────────────────────────────────

  const handleFileSelect = useCallback((e: ChangeEvent<HTMLInputElement>, which: 'boxes' | 'pallets') => {
    const file = e.target.files?.[0] ?? null
    if (which === 'boxes') setFileBoxes(file)
    else setFilePallets(file)
  }, [])

  const handlePreview = useCallback(async () => {
    if (!fileBoxes) return
    const result = await previewMutation.mutateAsync({
      file: fileBoxes,
      separator: config.separator,
      encoding: config.encoding,
    })
    setPreview(result)
    // Auto-detect separator from preview
    if (config.separator === 'auto' && result.detected_separator) {
      setConfig(prev => ({ ...prev, separator: result.detected_separator as SeparatorType }))
    }
    // Also preview pallets file in two_files mode
    if (needsTwoFiles && fileMode === 'two_files' && filePallets) {
      const palletResult = await previewMutation.mutateAsync({
        file: filePallets,
        separator: result.detected_separator as SeparatorType ?? config.separator,
        encoding: config.encoding,
      })
      setPreviewPallets(palletResult)
    } else {
      setPreviewPallets(null)
    }
    setPreviewTab('boxes')
    setStep('preview')
  }, [fileBoxes, filePallets, config.separator, config.encoding, previewMutation, needsTwoFiles, fileMode])

  const handleStartRowClick = useCallback((rowIndex: number) => {
    setConfig(prev => ({ ...prev, start_row: rowIndex }))
  }, [])

  const handleColumnClick = useCallback((colIndex: number) => {
    if (isDualColumnMode) {
      // In dual-column mode, assign to the active target
      if (columnTarget === 'boxes') {
        setConfig(prev => ({
          ...prev,
          column_index: colIndex,
          // Clear pallet column if same column was picked
          column_index_pallets: prev.column_index_pallets === colIndex ? undefined : prev.column_index_pallets,
        }))
      } else {
        setConfig(prev => ({
          ...prev,
          column_index_pallets: colIndex,
          // Clear box column if same column was picked
          column_index: prev.column_index === colIndex ? 0 : prev.column_index,
        }))
      }
    } else {
      setConfig(prev => ({ ...prev, column_index: colIndex }))
    }
  }, [isDualColumnMode, columnTarget])

  const handleSeparatorChange = useCallback(async (sep: SeparatorType) => {
    setConfig(prev => ({ ...prev, separator: sep }))
    // Re-preview with new separator
    if (fileBoxes) {
      const result = await previewMutation.mutateAsync({
        file: fileBoxes,
        separator: sep,
        encoding: config.encoding,
      })
      setPreview(result)
    }
    if (needsTwoFiles && fileMode === 'two_files' && filePallets) {
      const palletResult = await previewMutation.mutateAsync({
        file: filePallets,
        separator: sep,
        encoding: config.encoding,
      })
      setPreviewPallets(palletResult)
    }
  }, [fileBoxes, filePallets, config.encoding, previewMutation, needsTwoFiles, fileMode])

  const handleSubmit = useCallback(() => {
    if (!fileBoxes) return
    const finalConfig: ParseConfig = {
      ...config,
      file_mode: needsTwoFiles ? fileMode : undefined,
    }
    saveConfig(codeType, finalConfig)
    onSubmit({
      fileBoxes,
      filePallets: filePallets ?? undefined,
      config: finalConfig,
    })
  }, [fileBoxes, filePallets, config, fileMode, needsTwoFiles, codeType, onSubmit])

  // ── Can proceed checks ────────────────────────────────

  const canPreview = !!fileBoxes
  const canSubmit = !!fileBoxes
    && (!needsTwoFiles || fileMode === 'single_file' || !!filePallets)
    && (!isDualColumnMode || config.column_index_pallets !== undefined)

  const activePreview = previewTab === 'pallets' && previewPallets ? previewPallets : preview

  // ── Render ────────────────────────────────────────────

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-4xl">
      <Modal.Header onClose={onClose}>
        Импорт кодов из файла
      </Modal.Header>

      <Modal.Body className="min-h-[400px]">
        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6 text-sm">
          <StepIndicator label="Файл" active={step === 'file'} done={step !== 'file'} num={1} />
          <ChevronRight className="w-4 h-4 text-gray-300" />
          <StepIndicator label="Предпросмотр" active={step === 'preview'} done={step === 'submit'} num={2} />
          <ChevronRight className="w-4 h-4 text-gray-300" />
          <StepIndicator label="Загрузка" active={step === 'submit'} done={false} num={3} />
        </div>

        {/* ── Step 1: File selection ── */}
        {step === 'file' && (
          <div className="space-y-4">
            {/* File mode for ka_box_pallet */}
            {needsTwoFiles && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Режим файлов</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="fileMode"
                      value="two_files"
                      checked={fileMode === 'two_files'}
                      onChange={() => setFileMode('two_files')}
                      className="text-blue-600"
                    />
                    <span className="text-sm">Два файла (упаковки + паллеты)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="fileMode"
                      value="single_file"
                      checked={fileMode === 'single_file'}
                      onChange={() => setFileMode('single_file')}
                      className="text-blue-600"
                    />
                    <span className="text-sm">Один файл (все коды)</span>
                  </label>
                </div>
              </div>
            )}

            {/* Main file */}
            <FileDropZone
              label={needsTwoFiles && fileMode === 'two_files' ? 'Файл с кодами упаковок' : 'Файл с кодами'}
              file={fileBoxes}
              inputRef={fileBoxesInputRef}
              onSelect={(e) => handleFileSelect(e, 'boxes')}
            />

            {/* Second file for ka_box_pallet two_files mode */}
            {needsTwoFiles && fileMode === 'two_files' && (
              <FileDropZone
                label="Файл с кодами паллет"
                file={filePallets}
                inputRef={filePalletsInputRef}
                onSelect={(e) => handleFileSelect(e, 'pallets')}
              />
            )}

            {/* Separator preset (before preview) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Разделитель</label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={config.separator}
                onChange={(e) => setConfig(prev => ({ ...prev, separator: e.target.value as SeparatorType }))}
              >
                {(Object.entries(SEPARATOR_LABELS) as [SeparatorType, string][]).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {/* Encoding */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Кодировка</label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={config.encoding}
                onChange={(e) => setConfig(prev => ({ ...prev, encoding: e.target.value }))}
              >
                <option value="auto">Автоопределение</option>
                <option value="utf-8">UTF-8</option>
                <option value="cp1251">Windows-1251</option>
              </select>
            </div>
          </div>
        )}

        {/* ── Step 2: Preview + mapping ── */}
        {step === 'preview' && preview && (
          <div className="space-y-4">
            {/* Tab buttons for two-file mode */}
            {previewPallets && (
              <div className="flex gap-1 border-b">
                <button type="button" onClick={() => setPreviewTab('boxes')}
                  className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                    previewTab === 'boxes' ? 'border-blue-500 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}>
                  <Package className="w-3.5 h-3.5 inline mr-1" />Файл упаковок
                </button>
                <button type="button" onClick={() => setPreviewTab('pallets')}
                  className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                    previewTab === 'pallets' ? 'border-orange-500 text-orange-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}>
                  <Layers className="w-3.5 h-3.5 inline mr-1" />Файл паллет
                </button>
              </div>
            )}

            {/* Info bar */}
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>Строк: <strong>{activePreview!.total_lines}</strong></span>
              <span>Колонок: <strong>{activePreview!.column_count}</strong></span>
              <span>Кодировка: <strong>{activePreview!.encoding_detected}</strong></span>
              {activePreview!.is_excel && <span className="flex items-center gap-1"><FileSpreadsheet className="w-4 h-4" /> Excel</span>}
            </div>

            {/* Separator (can change and re-preview) */}
            {!activePreview!.is_excel && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Разделитель</label>
                <select
                  className="w-48 border rounded-md px-3 py-2 text-sm"
                  value={config.separator}
                  onChange={(e) => handleSeparatorChange(e.target.value as SeparatorType)}
                  disabled={previewMutation.isPending}
                >
                  {(Object.entries(SEPARATOR_LABELS) as [SeparatorType, string][]).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Instructions */}
            <div className="text-sm text-gray-500 bg-blue-50 rounded-md px-3 py-2">
              Кликните на <strong>строку</strong> чтобы указать начало данных (start row).
              {isDualColumnMode ? (
                <> Выберите целевую колонку ниже, затем кликните на <strong>заголовок</strong> в таблице.</>
              ) : (
                <> Кликните на <strong>заголовок колонки</strong> чтобы выбрать колонку с кодами.</>
              )}
            </div>

            {/* Dual-column target selector */}
            {isDualColumnMode && (
              <div className="flex gap-2">
                <button
                  type="button"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                    columnTarget === 'boxes'
                      ? 'bg-blue-100 border-blue-400 text-blue-800'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                  onClick={() => setColumnTarget('boxes')}
                >
                  <Package className="w-4 h-4" />
                  Упаковки
                  {config.column_index !== undefined && (
                    <span className="ml-1 px-1.5 py-0.5 bg-blue-200 text-blue-900 rounded text-xs">
                      Кол. {config.column_index + 1}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                    columnTarget === 'pallets'
                      ? 'bg-orange-100 border-orange-400 text-orange-800'
                      : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                  onClick={() => setColumnTarget('pallets')}
                >
                  <Layers className="w-4 h-4" />
                  Паллеты
                  {config.column_index_pallets !== undefined && (
                    <span className="ml-1 px-1.5 py-0.5 bg-orange-200 text-orange-900 rounded text-xs">
                      Кол. {config.column_index_pallets + 1}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Preview table */}
            <div className="overflow-auto max-h-[350px] border rounded-md">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-gray-400 w-10">#</th>
                    {Array.from({ length: activePreview!.column_count }, (_, i) => {
                      const isBoxCol = config.column_index === i
                      const isPalletCol = isDualColumnMode && config.column_index_pallets === i
                      return (
                        <th
                          key={i}
                          className={`px-2 py-1.5 text-left cursor-pointer hover:bg-blue-100 transition-colors ${
                            isBoxCol
                              ? 'bg-blue-200 text-blue-800 font-bold'
                              : isPalletCol
                                ? 'bg-orange-200 text-orange-800 font-bold'
                                : 'text-gray-500'
                          }`}
                          onClick={() => handleColumnClick(i)}
                          title={isDualColumnMode
                            ? `Назначить колонку ${i + 1} → ${columnTarget === 'boxes' ? 'упаковки' : 'паллеты'}`
                            : `Выбрать колонку ${i + 1}`
                          }
                        >
                          Кол. {i + 1}
                          {isBoxCol && (isDualColumnMode ? <span className="ml-1 inline-flex align-middle"><AppIcon name="box" className="h-3 w-3" /></span> : ' ✓')}
                          {isPalletCol && <span className="ml-1 inline-flex align-middle"><AppIcon name="pallet" className="h-3 w-3" /></span>}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {activePreview!.rows.map((row, rowIdx) => {
                    const isBeforeStart = rowIdx < config.start_row
                    const isStartRow = rowIdx === config.start_row
                    return (
                      <tr
                        key={rowIdx}
                        className={`cursor-pointer transition-colors ${
                          isBeforeStart
                            ? 'bg-gray-50 text-gray-300 line-through'
                            : isStartRow
                              ? 'bg-green-50 ring-1 ring-green-300'
                              : 'hover:bg-gray-50'
                        }`}
                        onClick={() => handleStartRowClick(rowIdx)}
                        title={`Начать с этой строки (row ${rowIdx})`}
                      >
                        <td className="px-2 py-1 text-gray-400 font-mono">{rowIdx + 1}</td>
                        {Array.from({ length: activePreview!.column_count }, (_, colIdx) => {
                          const isBoxCol = config.column_index === colIdx && !isBeforeStart
                          const isPalletCol = isDualColumnMode && config.column_index_pallets === colIdx && !isBeforeStart
                          return (
                            <td
                              key={colIdx}
                              className={`px-2 py-1 font-mono ${
                                isBoxCol
                                  ? 'bg-blue-50 font-semibold text-blue-900'
                                  : isPalletCol
                                    ? 'bg-orange-50 font-semibold text-orange-900'
                                    : ''
                              }`}
                            >
                              {row[colIdx] ?? ''}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Checksum strictness */}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!config.strict_checksum}
                onChange={(e) => setConfig(prev => ({ ...prev, strict_checksum: !e.target.checked }))}
                className="rounded text-blue-600"
              />
              <span>Допускать невалидный checksum (с предупреждением)</span>
            </label>

            {/* Mapping summary */}
            <div className="bg-gray-50 rounded-md px-3 py-2 text-sm flex items-center gap-4 flex-wrap">
              <span>Начало данных: <strong>строка {config.start_row + 1}</strong></span>
              {isDualColumnMode ? (
                <>
                  <span className="inline-flex items-center gap-1"><AppIcon name="box" className="h-3.5 w-3.5" /> Упаковки: <strong>Кол. {config.column_index + 1}</strong></span>
                  <span className="inline-flex items-center gap-1"><AppIcon name="pallet" className="h-3.5 w-3.5" /> Паллеты: <strong>{config.column_index_pallets !== undefined ? `Кол. ${config.column_index_pallets + 1}` : '—'}</strong></span>
                </>
              ) : (
                <span>Колонка кодов: <strong>#{config.column_index + 1}</strong></span>
              )}
              <span>
                Кодов к загрузке: <strong>{Math.max(0, activePreview!.total_lines - config.start_row)}</strong>
              </span>
            </div>
          </div>
        )}

        {/* ── Step 3 not rendered separately ── submit happens from preview */}
      </Modal.Body>

      <Modal.Footer>
        {step === 'file' && (
          <>
            <Button variant="ghost" onClick={onClose}>Отмена</Button>
            <Button
              onClick={handlePreview}
              disabled={!canPreview}
              isLoading={previewMutation.isPending}
            >
              Предпросмотр <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </>
        )}
        {step === 'preview' && (
          <>
            <Button variant="ghost" onClick={() => setStep('file')}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Назад
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              isLoading={isSubmitting}
            >
              <Upload className="w-4 h-4 mr-1" /> Загрузить коды
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}

// ═══════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════

function StepIndicator({ label, active, done, num }: { label: string; active: boolean; done: boolean; num: number }) {
  return (
    <div className={`flex items-center gap-1.5 ${active ? 'text-blue-600 font-medium' : done ? 'text-green-600' : 'text-gray-400'}`}>
      <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
        active ? 'bg-blue-100 text-blue-700' : done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
      }`}>
        {done ? <CheckCircle className="w-4 h-4" /> : num}
      </span>
      {label}
    </div>
  )
}

function FileDropZone({
  label,
  file,
  inputRef,
  onSelect,
}: {
  label: string
  file: File | null
  inputRef: React.RefObject<HTMLInputElement | null>
  onSelect: (e: ChangeEvent<HTMLInputElement>) => void
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div
        className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".txt,.csv,.xlsx,.xls"
          className="hidden"
          onChange={onSelect}
        />
        {file ? (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
            {file.name.match(/\.xlsx?$/i)
              ? <FileSpreadsheet className="w-5 h-5 text-green-600" />
              : <FileText className="w-5 h-5 text-blue-600" />
            }
            <span className="font-medium">{file.name}</span>
            <span className="text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
          </div>
        ) : (
          <div className="text-gray-400">
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Нажмите для выбора файла</p>
            <p className="text-xs mt-1">.txt, .csv, .xlsx</p>
          </div>
        )}
      </div>
    </div>
  )
}
